import * as z from 'zod';

export const CreatePolicyRequest = z.object({
  policy: z
    .object({
      _id: z
        .string()
        .describe("The policy's id")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Current state of this policy.\nEach time the policy is modified, its `revision` changes.\nFor an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      enabled: z
        .boolean()
        .describe('The active state of this policy - is it enabled or not')
        .optional(),
      locationId: z
        .string()
        .describe('The location id this policy is related to')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      rules: z
        .array(
          z.object({
            volumeLimit: z
              .number()
              .int()
              .describe('The volume limit')
              .min(1)
              .optional(),
            includeContactlessDineinOrders: z
              .boolean()
              .describe(
                'Should the volume limit include contactless dine-in orders.'
              )
              .optional(),
            enforcementTimes: z
              .array(
                z.object({
                  minuteOfWeek: z
                    .number()
                    .int()
                    .describe('The minute of the week the interval starts')
                    .min(0)
                    .max(10080)
                    .optional(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The duration in minutes of the interval, starting from the minute_of_week'
                    )
                    .min(1)
                    .max(10080)
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .min(1)
        .max(1),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
    })
    .describe('The policy to be created'),
});
export const CreatePolicyResponse = z.object({
  _id: z
    .string()
    .describe("The policy's id")
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      'Current state of this policy.\nEach time the policy is modified, its `revision` changes.\nFor an update operation to succeed, you MUST pass the latest revision'
    )
    .optional()
    .nullable(),
  enabled: z
    .boolean()
    .describe('The active state of this policy - is it enabled or not')
    .optional(),
  locationId: z
    .string()
    .describe('The location id this policy is related to')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  rules: z
    .array(
      z.object({
        volumeLimit: z
          .number()
          .int()
          .describe('The volume limit')
          .min(1)
          .optional(),
        includeContactlessDineinOrders: z
          .boolean()
          .describe(
            'Should the volume limit include contactless dine-in orders.'
          )
          .optional(),
        enforcementTimes: z
          .array(
            z.object({
              minuteOfWeek: z
                .number()
                .int()
                .describe('The minute of the week the interval starts')
                .min(0)
                .max(10080)
                .optional(),
              durationInMinutes: z
                .number()
                .int()
                .describe(
                  'The duration in minutes of the interval, starting from the minute_of_week'
                )
                .min(1)
                .max(10080)
                .optional(),
            })
          )
          .optional(),
      })
    )
    .min(1)
    .max(1)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
});
export const GetPolicyRequest = z.object({
  options: z
    .object({
      locationId: z
        .string()
        .describe('The location_id the policy belongs to')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetPolicyResponse = z.object({
  policy: z
    .object({
      _id: z
        .string()
        .describe("The policy's id")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Current state of this policy.\nEach time the policy is modified, its `revision` changes.\nFor an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      enabled: z
        .boolean()
        .describe('The active state of this policy - is it enabled or not')
        .optional(),
      locationId: z
        .string()
        .describe('The location id this policy is related to')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      rules: z
        .array(
          z.object({
            volumeLimit: z
              .number()
              .int()
              .describe('The volume limit')
              .min(1)
              .optional(),
            includeContactlessDineinOrders: z
              .boolean()
              .describe(
                'Should the volume limit include contactless dine-in orders.'
              )
              .optional(),
            enforcementTimes: z
              .array(
                z.object({
                  minuteOfWeek: z
                    .number()
                    .int()
                    .describe('The minute of the week the interval starts')
                    .min(0)
                    .max(10080)
                    .optional(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The duration in minutes of the interval, starting from the minute_of_week'
                    )
                    .min(1)
                    .max(10080)
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .min(1)
        .max(1)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
    })
    .describe('The retrieved Policy')
    .optional(),
});
export const UpdatePolicyRequest = z.object({
  _id: z
    .string()
    .describe("The policy's id")
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  policy: z
    .object({
      _id: z
        .string()
        .describe("The policy's id")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Current state of this policy.\nEach time the policy is modified, its `revision` changes.\nFor an update operation to succeed, you MUST pass the latest revision'
        ),
      enabled: z
        .boolean()
        .describe('The active state of this policy - is it enabled or not')
        .optional(),
      locationId: z
        .string()
        .describe('The location id this policy is related to')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      rules: z
        .array(
          z.object({
            volumeLimit: z
              .number()
              .int()
              .describe('The volume limit')
              .min(1)
              .optional(),
            includeContactlessDineinOrders: z
              .boolean()
              .describe(
                'Should the volume limit include contactless dine-in orders.'
              )
              .optional(),
            enforcementTimes: z
              .array(
                z.object({
                  minuteOfWeek: z
                    .number()
                    .int()
                    .describe('The minute of the week the interval starts')
                    .min(0)
                    .max(10080)
                    .optional(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The duration in minutes of the interval, starting from the minute_of_week'
                    )
                    .min(1)
                    .max(10080)
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .min(1)
        .max(1),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
    })
    .describe('Policy to be updated, may be partial'),
});
export const UpdatePolicyResponse = z.object({
  policy: z
    .object({
      _id: z
        .string()
        .describe("The policy's id")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Current state of this policy.\nEach time the policy is modified, its `revision` changes.\nFor an update operation to succeed, you MUST pass the latest revision'
        )
        .optional()
        .nullable(),
      enabled: z
        .boolean()
        .describe('The active state of this policy - is it enabled or not')
        .optional(),
      locationId: z
        .string()
        .describe('The location id this policy is related to')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      rules: z
        .array(
          z.object({
            volumeLimit: z
              .number()
              .int()
              .describe('The volume limit')
              .min(1)
              .optional(),
            includeContactlessDineinOrders: z
              .boolean()
              .describe(
                'Should the volume limit include contactless dine-in orders.'
              )
              .optional(),
            enforcementTimes: z
              .array(
                z.object({
                  minuteOfWeek: z
                    .number()
                    .int()
                    .describe('The minute of the week the interval starts')
                    .min(0)
                    .max(10080)
                    .optional(),
                  durationInMinutes: z
                    .number()
                    .int()
                    .describe(
                      'The duration in minutes of the interval, starting from the minute_of_week'
                    )
                    .min(1)
                    .max(10080)
                    .optional(),
                })
              )
              .optional(),
          })
        )
        .min(1)
        .max(1)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
    })
    .describe('The updated Policy')
    .optional(),
});
